import FormInput from "../../inputs/FormInput.tsx";
import Button from "../../buttons/Button.tsx";
import { ButtonSize, ButtonType, ButtonVariant } from "../../../types/button.ts";
import React, { ChangeEvent, FormEvent, useContext, useState } from "react";
import { InputType } from "../../../types/input.ts";
import { useAuth } from "../../../hooks/useAuth.tsx";
import { AppMainStateContext } from "../../../contexts/AppMainStateContext.tsx";
import { NotificationStatus } from "../../notification/Notification.tsx";
import { useNotification } from "../../../providers/NotificationProvider.tsx";
import ButtonLink from "../../buttons/ButtonLink.tsx";
import { UnauthorizedMessageStatusTypes } from "../../../exceptions/unauthorizedException.ts";
import { ForbiddenMessageStatusTypes } from "../../../exceptions/forbiddenException.ts";
import CircleLoader from "../../loaders/CircleLoader.tsx";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useNavigate, useSearchParams } from "react-router-dom";
import BrowserDetectHelper from "../../../helpers/browserDetectHelper.ts";
import { BadRequestMessageStatusTypes } from "../../../exceptions/badRequestException.ts";
import ValidatorsHelper from "../../../helpers/validatorsHelper.ts";
import api from "../../../newApi/apiNet.ts";

const Login = () => {
    const _authService = useAuth();
    const _recaptchaService = useGoogleReCaptcha();
    const _navigate = useNavigate();
    const [queryParams] = useSearchParams();
    const [input, setInput] = useState({
        email: "",
        password: "",
        rememberMe: true,
    });
    const [isError, setIsError] = useState(false);
    const [isEmailValid, setisEmailValid] = useState(false);
    const [isPasswordValid, setisPasswordValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { closeModal } = useContext(AppMainStateContext);
    const { showNotification, setIsNotificationVisible } = useNotification();
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.name === "email") {
            e.target.value = e.target.value.replace(" ", "");
            setisEmailValid(ValidatorsHelper.isEmailValid(e.target.value));
        }
        if (e.target.name === "password") {
            setisPasswordValid(e.target.value.length > 3);
        }
        setInput((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))
    };
    const handleRememberMeChange = (e: ChangeEvent<HTMLInputElement>) =>
        setInput((prev) => ({ ...prev, rememberMe: e.target.checked }))
    const handleSubmitEvent = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isEmailValid && !isPasswordValid) {
            setIsError(true);
            return;
        }
        setIsError(false);
        setIsLoading(true);
        if (_recaptchaService.executeRecaptcha) {
            try {
                if (import.meta.env.VITE_BACKEND_TYPE === 'php') {
                    await api.getCsrfCookie();
                }
                const recaptchaToken = await _recaptchaService.executeRecaptcha('login');
                const osInfo = BrowserDetectHelper.getOsInfo() + ", " + BrowserDetectHelper.getBrowserInfo();
                if (recaptchaToken) {
                    if (import.meta.env.VITE_BACKEND_TYPE === 'php') {
                        await _authService.loginPHP(recaptchaToken, 72306, input.password, input.rememberMe, osInfo);
                    } else {
                        await _authService.login(recaptchaToken, input.email.replace(" ", ""), input.password, input.rememberMe, osInfo);
                    }
                }
                setIsError(false);
                closeModal();
                showNotification(
                    "Sukces",
                    "Zalogowano pomyślnie",
                    NotificationStatus.success
                );
                setIsNotificationVisible(true);
                const redirectTo = queryParams.get("redirectTo");
                _navigate(redirectTo ? redirectTo : "/");
            } catch (e: unknown) {
                const error = e as { code: number, status: UnauthorizedMessageStatusTypes | ForbiddenMessageStatusTypes | BadRequestMessageStatusTypes };
                if (error.code === 400 && error.status === "EMAIL_IS_INCORRECT")
                    showNotification(
                        "Niepowdzenie",
                        "Wprowadzony adres email jest nieprawidłowy.",
                        NotificationStatus.failure
                    );
                else if (error.code === 400 && error.status === "INVALID_RECAPTCHA_TOKEN")
                    showNotification(
                        "Niepowdzenie",
                        "Weryfikacja zabezpieczenia reCAPTCHA nie powiodła się. Proszę ponownie kliknąć przycisk „Zaloguj się” lub odświeżyć stronę i wprowadzić dane logowania za pomocą klawiatury.",
                        NotificationStatus.failure
                    );
                else if (error.code === 401 && error.status === "INVALID_CREDENTIALS")
                    showNotification(
                        "Niepowdzenie",
                        "Adres email lub hasło jest nieprawidłowe",
                        NotificationStatus.failure
                    );
                else if (error.code === 403 && error.status === "ACCOUNT_NOT_CONFIRMED")
                    showNotification(
                        "Niepowdzenie",
                        "Konto nie zostało aktywowane. Kliknij w link aktywacyjny w e-mailu, który otrzymałeś po rejestracji.",
                        NotificationStatus.failure
                    );
                else if (error.code === 403 && error.status === "ACCOUNT_NOT_CONFIRMED_SENT_NEW_MAIL")
                    showNotification(
                        "Niepowdzenie",
                        "Konto nie zostało aktywowane. Wysłaliśmy nowy e-mail. Kliknij w link aktywacyjny w e-mailu.",
                        NotificationStatus.failure
                    );
                else if (error.code === 403 && error.status === "DEVICE_LIMIT_REACHED")
                    showNotification(
                        "Niepowdzenie",
                        "Osiągnięto limit urządzeń. Usuń nieużywane urządzenia w portalu klienta bok.beskidmedia.pl",
                        NotificationStatus.failure
                    );
                else
                    showNotification(
                        "Niepowdzenie",
                        "Wystąpił błąd po stronie serwera, spróbuj ponownie za kilka minut",
                        NotificationStatus.failure
                    );
            } finally {
                setIsLoading(false);
            }
        }
    };

    // const handleSubmitEvent1 = async (e: FormEvent<HTMLFormElement>) => {
    //     e.preventDefault();
    //     const result = await api.getToken(2);
    //     console.log(result);

    // }

    const handleLogoutClick = async () => {
        try {
            await _authService.logout();
            showNotification(
                "Sukces",
                "Zostałeś wylogowany pomyślnie",
                NotificationStatus.success
            );
            closeModal();
        } catch (error) {
            showNotification(
                "Niepowodzenie",
                "Wylogowanie nie powiodło się. Spróbuj ponownie.",
                NotificationStatus.failure
            );
            closeModal();
        }
    };


    return (
        <>
            {_authService.token ? (
                <div className={"p-5 space-y-3"}>
                    <p className={"font-extralight"}>
                        Witaj,{" "}
                        <span className={"font-medium"}>{_authService.user?.userName}</span>
                        !
                    </p>
                    <div className="space-y-3">
                        <p>Rozmiar czcionki</p>
                        <div className="flex space-x-3 items-end">
                            <div className="flex flex-col space-y-2">
                                <button className="rounded-full py-1 px-6 bg-neutral-800 text-neutral-300 text-sm">
                                    Aa
                                </button>
                                <p className="text-sm font-light text-neutral-300">
                                    Pomniejszona
                                </p>
                            </div>
                            <div className="flex flex-col space-y-2">
                                <Button size={ButtonSize.BASE} variant={ButtonVariant.PRIMARY}>
                                    Aa
                                </Button>
                                <p className="text-sm font-light text-neutral-300">
                                    Standardowa
                                </p>
                            </div>
                            <div className="flex flex-col space-y-2">
                                <button className="rounded-full py-1 px-6 bg-neutral-800 text-neutral-300 text-lg">
                                    Aa
                                </button>
                                <p className="text-sm font-light text-neutral-300">
                                    Powiększona
                                </p>
                            </div>
                        </div>
                    </div>
                    <Button
                        size={ButtonSize.BASE}
                        variant={ButtonVariant.PRIMARY}
                        onClick={handleLogoutClick}
                    >
                        Wyloguj
                    </Button>
                </div>
            ) :
                <div
                    className={`flex text-left flex-col items-center modal-opacity-ease-in-out-animation w-full`}
                >
                    <form
                        action="/login"
                        onSubmit={(e) => handleSubmitEvent(e)}
                        className={"w-full"}
                    >
                        <div className={"flex flex-col"}>
                            <span className={"flex flex-col space-y-3"}>
                                <FormInput
                                    label={"Email"}
                                    name={Object.keys(input)[0]}
                                    type={InputType.TEXT}
                                    value={input.email}
                                    onInputChange={handleInputChange}
                                    placeholder={"Wprowadź adres email"}
                                    className={"bg-neutral-700 ring-offset-neutral-900 py-2 border-transparent"}
                                />
                                {isError && !isEmailValid &&
                                    <p className="text-red-500">Adres email jest nieprawidłowy</p>
                                }
                                <FormInput
                                    label={"Hasło"}
                                    name={Object.keys(input)[1]}
                                    type={InputType.PASSWORD}
                                    value={input.password}
                                    onInputChange={handleInputChange}
                                    placeholder={"Wprowadź hasło"}
                                    className={"bg-neutral-700 ring-offset-neutral-900 py-2 border-transparent"}
                                />
                                {isError && !isPasswordValid &&
                                    <p className="text-red-500">Wprowadź hasło</p>
                                }
                            </span>
                            <label htmlFor="rememberMe" className={"relative flex my-5 items-center"}>
                                <input type="checkbox" name="rememberMe" id="rememberMe" checked={input.rememberMe}
                                    onChange={handleRememberMeChange}
                                    className={"relative appearance-none peer shrink-0 rounded-full overflow-hidden w-5 h-5 outline-none ring-transparent ring-2 ring-offset-2 border border-neutral-100 ring-offset-neutral-900 checked:bg-[#008DD2] checked:border-0"} />
                                <p className={"ml-2 text-sm text-neutral-200 font-medium mt-0.5 select-none"}>Zapamiętaj
                                    mnie <span className={"font-light"}>(odznacz jeżeli z komputera korzystają również inni użytkownicy)</span>
                                </p>
                                <svg
                                    className="absolute w-4 h-4 ml-0.5 mt-0.5 hidden peer-checked:block text-neutral-100"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                            </label>
                            <p className={"text-xs text-neutral-400 mb-4"}>Ta strona korzysta z usługi reCAPTCHA. Usługę
                                reguluje <ButtonLink to={"https://policies.google.com/terms"}
                                    variant={ButtonVariant.SECONDARY}
                                    targetType={"_blank"}
                                    className={"text-xs pl-0 pr-0 pt-0 pb-0"}>regulamin
                                    usługi</ButtonLink> Google.</p>
                            <Button
                                type={ButtonType.SUBMIT}
                                size={ButtonSize.LARGE}
                                variant={ButtonVariant.PRIMARY}
                                disabled={isLoading}
                                className="flex justify-center items-center mt-1 ring-offset-neutral-900 py-2"
                            >
                                {isLoading ?
                                    <>
                                        <CircleLoader />
                                        <span className="ml-3">Logowanie...</span>
                                    </>
                                    :
                                    "Zaloguj się"
                                }
                            </Button>
                        </div>
                    </form>
                    <ButtonLink to={"/odzyskiwanie-hasla"} variant={ButtonVariant.SECONDARY}
                        className={"pl-0 pr-0 mt-3 pb-0 mb-0 text-[#008dd2] hover:text-sky-500"}>
                        Nie pamiętasz hasła?
                    </ButtonLink>
                </div>
            }
        </>
    );
};

export default Login;